import { ResourceLabelContentArg } from '@fullcalendar/resource';
import { Avatar, Box, Typography } from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { PetsCountBadge } from 'components/MultiSlot/PetsCountBadge';
import { getStaffSlotsByDate } from 'components/StaffSchedule/utils/StaffScheduleUtils';
import { useMasterData } from 'hooks';
import { BusinessAreaType, SERVICES_BUSINESS_AREAS } from 'model/Service';
import { Moment } from 'moment-timezone';
import React from 'react';

export type ResourceHeaderFullCalendarProps = Omit<ResourceLabelContentArg, 'date'> & {
    date: Moment;
    businessArea: BusinessAreaType;
    showPetsCountBadge: boolean;
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            gap: 8,
            display: 'flex',
            alignItems: 'center',
            padding: '8px 4px'
        },
        avatar: {
            [theme.breakpoints.down('sm')]: {
                width: '30px',
                height: '30px'
            },
            [theme.breakpoints.only('md')]: {
                width: '34px',
                height: '34px'
            },
            [theme.breakpoints.up('lg')]: {
                width: '42px',
                height: '42px'
            }
        },
        name: {
            fontWeight: 500,
            color: '#222222',
            fontFamily: 'Poppins',
            [theme.breakpoints.down('sm')]: {
                fontSize: '11px',
                lineHeight: '14px'
            },
            [theme.breakpoints.only('md')]: {
                fontSize: '12px',
                lineHeight: '15px'
            },
            [theme.breakpoints.only('lg')]: {
                fontSize: '14px',
                lineHeight: '17px'
            },
            [theme.breakpoints.only('xl')]: {
                fontSize: '16px',
                lineHeight: '19px'
            }
        }
    })
);

export const ResourceHeaderFullCalendar: React.FC<ResourceHeaderFullCalendarProps> = props => {
    const classes = useStyles();
    const masterData = useMasterData();

    const isUnassigned = props.resource.extendedProps.isUnassigned;
    const staffColorId: string | null = props.resource.extendedProps.staffColor ?? null;
    const ringColor =
        !isUnassigned && staffColorId
            ? masterData.serviceColors.find(color => color.id === staffColorId)?.name ?? staffColorId
            : undefined;

    const maxPetsCount = React.useMemo(() => {
        if (isUnassigned) return 0;
        return getStaffSlotsByDate(props.resource.extendedProps.slots, props.date).reduce((petsCount, slot) => {
            return petsCount + slot.petsCount;
        }, 0);
    }, [props.resource, props.date, isUnassigned]);

    return (
        <Box className={classes.container}>
            <Avatar
                className={classes.avatar}
                style={ringColor ? { boxShadow: `0 0 0 3px #fff, 0 0 0 5px ${ringColor}` } : undefined}
                src={isUnassigned ? undefined : props.resource.extendedProps.avatar}
            />
            <Typography className={classes.name}>
                {isUnassigned
                    ? 'Unassigned'
                    : `${props.resource.extendedProps.firstName} ${props.resource.extendedProps.lastName}`}
            </Typography>

            {!isUnassigned && props.showPetsCountBadge && props.businessArea === SERVICES_BUSINESS_AREAS.GROOMING && (
                <PetsCountBadge
                    maxPetsCount={maxPetsCount}
                    petsCount={props.resource.extendedProps.appointmentsCount}
                />
            )}
        </Box>
    );
};

export default ResourceHeaderFullCalendar;
